
import { useState } from 'react';
import { Link } from 'react-router-dom';

export default function ConsultationSchedulingPage() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    consultationType: '',
    preferredDate: '',
    preferredTime: '',
    timezone: '',
    dealershipInterest: '',
    currentBusiness: '',
    investmentTimeline: '',
    message: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const formBody = new URLSearchParams();
      Object.entries(formData).forEach(([key, value]) => {
        formBody.append(key, value);
      });

      const response = await fetch('https://readdy.ai/api/form/consultation-scheduling', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString()
      });

      if (response.ok) {
        setShowSuccess(true);
      } else {
        alert('There was an error scheduling your consultation. Please try again.');
      }
    } catch (error) {
      alert('There was an error scheduling your consultation. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  if (showSuccess) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-50 to-teal-50 py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-xl shadow-lg p-8 text-center">
            <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <i className="ri-calendar-check-line text-2xl text-emerald-600"></i>
            </div>
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Consultation Request Received!</h2>
            <p className="text-lg text-gray-600 mb-8">
              Thank you for scheduling a consultation. Our dealership team will contact you within 24 hours to confirm your appointment time and provide meeting details.
            </p>
            <div className="bg-emerald-50 rounded-lg p-6 mb-8">
              <h3 className="font-semibold text-emerald-800 mb-2">What happens next?</h3>
              <div className="text-left space-y-2 text-emerald-700">
                <div className="flex items-center space-x-2">
                  <i className="ri-check-line text-emerald-600"></i>
                  <span>We'll call you within 24 hours to confirm your preferred time</span>
                </div>
                <div className="flex items-center space-x-2">
                  <i className="ri-check-line text-emerald-600"></i>
                  <span>You'll receive a calendar invitation with meeting details</span>
                </div>
                <div className="flex items-center space-x-2">
                  <i className="ri-check-line text-emerald-600"></i>
                  <span>We'll send preparation materials before the call</span>
                </div>
              </div>
            </div>
            <div className="space-y-4">
              <Link
                to="/dealership"
                className="inline-block bg-emerald-600 text-white px-8 py-3 rounded-lg font-semibold hover:bg-emerald-700 transition-colors cursor-pointer whitespace-nowrap"
              >
                Back to Dealership
              </Link>
              <div className="text-sm text-gray-500">
                <p>Questions? Contact us at <a href="mailto:dealerships@glamping.com" className="text-emerald-600 hover:underline">dealerships@glamping.com</a></p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 to-teal-50 py-20">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            Schedule Your Consultation
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Book a personalized consultation with our dealership experts to discuss investment opportunities and business strategies
          </p>
        </div>

        <div className="bg-white rounded-xl shadow-lg p-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-8">
            <div className="text-center">
              <div className="w-12 h-12 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-user-line text-emerald-600 text-xl"></i>
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">Expert Guidance</h3>
              <p className="text-sm text-gray-600">Get personalized advice from our experienced dealership team</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-time-line text-emerald-600 text-xl"></i>
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">Flexible Scheduling</h3>
              <p className="text-sm text-gray-600">Choose a time that works best for your schedule</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-money-dollar-circle-line text-emerald-600 text-xl"></i>
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">No Cost</h3>
              <p className="text-sm text-gray-600">Complimentary consultation to explore opportunities</p>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6" data-readdy-form id="consultation-scheduling">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                  Full Name *
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-sm"
                />
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                  Email Address *
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-sm"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
                  Phone Number *
                </label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-sm"
                />
              </div>

              <div>
                <label htmlFor="company" className="block text-sm font-medium text-gray-700 mb-2">
                  Company/Organization
                </label>
                <input
                  type="text"
                  id="company"
                  name="company"
                  value={formData.company}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-sm"
                />
              </div>
            </div>

            <div>
              <label htmlFor="consultationType" className="block text-sm font-medium text-gray-700 mb-2">
                Consultation Type *
              </label>
              <select
                id="consultationType"
                name="consultationType"
                value={formData.consultationType}
                onChange={handleChange}
                required
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-sm pr-8"
              >
                <option value="">Select consultation type</option>
                <option value="dealership-overview">Dealership Overview & Opportunities</option>
                <option value="investment-planning">Investment Planning & ROI Analysis</option>
                <option value="product-selection">Product Selection & Customization</option>
                <option value="territory-discussion">Territory Rights & Market Analysis</option>
                <option value="technical-support">Technical Support & Training</option>
                <option value="marketing-strategy">Marketing Strategy & Campaigns</option>
              </select>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div>
                <label htmlFor="preferredDate" className="block text-sm font-medium text-gray-700 mb-2">
                  Preferred Date *
                </label>
                <input
                  type="date"
                  id="preferredDate"
                  name="preferredDate"
                  value={formData.preferredDate}
                  onChange={handleChange}
                  required
                  min={new Date().toISOString().split('T')[0]}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-sm"
                />
              </div>

              <div>
                <label htmlFor="preferredTime" className="block text-sm font-medium text-gray-700 mb-2">
                  Preferred Time *
                </label>
                <select
                  id="preferredTime"
                  name="preferredTime"
                  value={formData.preferredTime}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-sm pr-8"
                >
                  <option value="">Select time</option>
                  <option value="9:00 AM">9:00 AM</option>
                  <option value="10:00 AM">10:00 AM</option>
                  <option value="11:00 AM">11:00 AM</option>
                  <option value="12:00 PM">12:00 PM</option>
                  <option value="1:00 PM">1:00 PM</option>
                  <option value="2:00 PM">2:00 PM</option>
                  <option value="3:00 PM">3:00 PM</option>
                  <option value="4:00 PM">4:00 PM</option>
                  <option value="5:00 PM">5:00 PM</option>
                </select>
              </div>

              <div>
                <label htmlFor="timezone" className="block text-sm font-medium text-gray-700 mb-2">
                  Timezone *
                </label>
                <select
                  id="timezone"
                  name="timezone"
                  value={formData.timezone}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-sm pr-8"
                >
                  <option value="">Select timezone</option>
                  <option value="EST">Eastern (EST)</option>
                  <option value="CST">Central (CST)</option>
                  <option value="MST">Mountain (MST)</option>
                  <option value="PST">Pacific (PST)</option>
                  <option value="GMT">GMT</option>
                  <option value="CET">Central European (CET)</option>
                </select>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label htmlFor="dealershipInterest" className="block text-sm font-medium text-gray-700 mb-2">
                  Dealership Package Interest
                </label>
                <select
                  id="dealershipInterest"
                  name="dealershipInterest"
                  value={formData.dealershipInterest}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-sm pr-8"
                >
                  <option value="">Select package</option>
                  <option value="starter">Starter Dealership - $50,000</option>
                  <option value="growth">Growth Dealership - $100,000</option>
                  <option value="premium">Premium Dealership - $200,000</option>
                  <option value="exploring">Still exploring options</option>
                </select>
              </div>

              <div>
                <label htmlFor="investmentTimeline" className="block text-sm font-medium text-gray-700 mb-2">
                  Investment Timeline
                </label>
                <select
                  id="investmentTimeline"
                  name="investmentTimeline"
                  value={formData.investmentTimeline}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-sm pr-8"
                >
                  <option value="">Select timeline</option>
                  <option value="immediate">Ready to invest immediately</option>
                  <option value="1-3-months">Within 1-3 months</option>
                  <option value="3-6-months">Within 3-6 months</option>
                  <option value="6-12-months">Within 6-12 months</option>
                  <option value="exploring">Just exploring for now</option>
                </select>
              </div>
            </div>

            <div>
              <label htmlFor="currentBusiness" className="block text-sm font-medium text-gray-700 mb-2">
                Current Business Experience
              </label>
              <select
                id="currentBusiness"
                name="currentBusiness"
                value={formData.currentBusiness}
                onChange={handleChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-sm pr-8"
              >
                <option value="">Select experience level</option>
                <option value="first-time">First-time business owner</option>
                <option value="small-business">Small business owner</option>
                <option value="franchise">Franchise experience</option>
                <option value="hospitality">Hospitality industry experience</option>
                <option value="real-estate">Real estate experience</option>
                <option value="other">Other business experience</option>
              </select>
            </div>

            <div>
              <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">
                Additional Information
              </label>
              <textarea
                id="message"
                name="message"
                value={formData.message}
                onChange={handleChange}
                rows={4}
                maxLength={500}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-sm resize-none"
                placeholder="Tell us about your specific goals, questions, or any topics you'd like to discuss during the consultation..."
              />
              <p className="text-xs text-gray-500 mt-1">{formData.message.length}/500 characters</p>
            </div>

            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full bg-emerald-600 text-white py-4 px-6 rounded-lg font-semibold hover:bg-emerald-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap text-lg"
            >
              {isSubmitting ? 'Scheduling...' : 'Schedule Consultation'}
            </button>

            <p className="text-xs text-gray-500 text-center">
              By scheduling a consultation, you agree to be contacted by our dealership team to confirm your appointment.
            </p>
          </form>
        </div>
      </div>
    </div>
  );
}
